import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { FiRefreshCw, FiUsers, FiDownload } from 'react-icons/fi';
import { API_URL } from '../config/api';
import './ListarAcessos.css';

/** Desbloqueia colunas sensíveis na lista (ideal: validar no servidor). */
const SENHA_AUDITORIA_ACESSOS = '0#$TQVASewq2s$#SGS';
const SENHA_GERAR_BACKUP = 'NLX@1978';

const ListarAcessos = () => {
  const [acessos, setAcessos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroData, setFiltroData] = useState('');
  const [mostrarTodosDados, setMostrarTodosDados] = useState(false);
  const [modalAuditoriaAberto, setModalAuditoriaAberto] = useState(false);
  const [senhaAuditoria, setSenhaAuditoria] = useState('');
  const [erroSenhaAuditoria, setErroSenhaAuditoria] = useState('');

  const fetchAcessos = async () => {
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      if (!token) {
        window.location.href = '/sysconect/adm-login';
        return;
      }
      const response = await axios.get(`${API_URL}/api/acessos/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAcessos(response.data || []);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/sysconect/adm-login';
        return;
      }
      setError('Erro ao listar acessos. Tente novamente.');
      setAcessos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAcessos();
  }, []);

  const formatDateOnly = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      const parts = String(value).split('-');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return '-';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const formatDateForInput = (value) => {
    if (!value) return '';
    
    // Se já está no formato YYYY-MM-DD, retornar direto
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return value.split('T')[0].split(' ')[0];
    }
    
    // Tentar criar objeto Date
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    // Tentar parsear formato DD-MM-YYYY
    const parts = String(value).split('-');
    if (parts.length === 3 && parts[0].length === 2) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    
    return '';
  };

  const acessosFiltrados = useMemo(() => {
    if (!filtroData) return acessos;
    
    return acessos.filter((acesso) => {
      const dataAcesso = formatDateForInput(acesso.data_acesso);
      return dataAcesso === filtroData;
    });
  }, [acessos, filtroData]);

  const abrirModalAuditoria = () => {
    setSenhaAuditoria('');
    setErroSenhaAuditoria('');
    setModalAuditoriaAberto(true);
  };

  const fecharModalAuditoria = () => {
    setModalAuditoriaAberto(false);
    setSenhaAuditoria('');
    setErroSenhaAuditoria('');
  };

  const confirmarSenhaAuditoria = (e) => {
    e.preventDefault();
    if (senhaAuditoria === SENHA_AUDITORIA_ACESSOS) {
      setMostrarTodosDados(true);
      fecharModalAuditoria();
      return;
    }
    setErroSenhaAuditoria('Senha incorreta.');
  };

  const onToggleTodosDados = (marcado) => {
    if (marcado) {
      abrirModalAuditoria();
      return;
    }
    setMostrarTodosDados(false);
  };

  const toCsvCell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

  const downloadCsv = (nomeArquivo, cabecalho, linhas) => {
    const csvContent = [cabecalho.map(toCsvCell).join(','), ...linhas].join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', nomeArquivo);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const parseDateTimeAcesso = (acesso) => {
    const data = (acesso?.data_acesso || '').toString().trim();
    const hora = (acesso?.hora_acesso || '00:00:00').toString().trim() || '00:00:00';
    if (!data) return 0;
    const iso = data.includes('T') ? data : `${data}T${hora}`;
    const dt = new Date(iso);
    return Number.isNaN(dt.getTime()) ? 0 : dt.getTime();
  };

  const gerarBackup = () => {
    const senha = window.prompt('Digite a senha para gerar o backup:');
    if (senha === null) return;
    if (senha !== SENHA_GERAR_BACKUP) {
      alert('Senha incorreta. Backup não gerado.');
      return;
    }

    const dadosParaBackup = filtroData ? acessosFiltrados : acessos;
    
    if (dadosParaBackup.length === 0) {
      alert('Não há dados para gerar backup.');
      return;
    }

    const dataAtual = new Date().toISOString().split('T')[0];
    const sufixo = filtroData || dataAtual;

    // Arquivo 1: Nome, CPF e Telefone em ordem alfabética (por nome)
    const porNome = [...dadosParaBackup].sort((a, b) =>
      String(a?.nome || '').localeCompare(String(b?.nome || ''), 'pt-BR', { sensitivity: 'base' })
    );
    const linhasPorNome = porNome.map((acesso) =>
      [acesso.nome || '', acesso.cpf || '', acesso.celular || '']
        .map(toCsvCell)
        .join(',')
    );
    downloadCsv(
      `backup_acessos_por_nome_${sufixo}.csv`,
      ['Nome', 'CPF', 'Telefone'],
      linhasPorNome
    );

    // Arquivo 2: Todos os dados do mais antigo para o mais novo (data/hora)
    const porDataHora = [...dadosParaBackup].sort((a, b) => parseDateTimeAcesso(a) - parseDateTimeAcesso(b));
    const linhasPorDataHora = porDataHora.map((acesso) =>
      [
        acesso.nome || '',
        acesso.server_name_resolvido || acesso.server_name || '-',
        acesso.cpf || '',
        acesso.celular || '',
        acesso.mac_address || '',
        acesso.ip_address || '',
        acesso.public_ip || '',
        formatDateOnly(acesso.data_acesso),
        acesso.hora_acesso || ''
      ]
        .map(toCsvCell)
        .join(',')
    );
    downloadCsv(
      `backup_acessos_por_data_hora_${sufixo}.csv`,
      ['Nome', 'Hotspot/Torre', 'CPF', 'Celular', 'MAC', 'IP', 'IP Público', 'Data', 'Hora'],
      linhasPorDataHora
    );
  };

  if (loading) {
    return <div className="loading">Carregando acessos...</div>;
  }

  return (
    <div className="listar-acessos">
      <div className="acessos-header">
        <div>
          <h1>Lista de Acessos</h1>
          <p>Visualize todos os acessos registrados no portal</p>
        </div>
        <div className="acessos-actions">
          <div className="total-acessos">
            <FiUsers /> {acessosFiltrados.length} {acessosFiltrados.length === 1 ? 'acesso' : 'acessos'}
            {filtroData && ` (de ${acessos.length} total)`}
          </div>
          <button className="refresh-btn" onClick={fetchAcessos} title="Recarregar">
            <FiRefreshCw /> Recarregar
          </button>
          <button className="backup-btn" onClick={gerarBackup} title="Gerar Backup">
            <FiDownload /> Gerar Backup
          </button>
        </div>
      </div>

      <div className="filtro-data-container">
        <label htmlFor="filtro-data">Filtrar por data:</label>
        <input
          type="date"
          id="filtro-data"
          value={filtroData}
          onChange={(e) => setFiltroData(e.target.value)}
          className="filtro-data-input"
        />
        {filtroData && (
          <button 
            className="limpar-filtro-btn" 
            onClick={() => setFiltroData('')}
            title="Limpar filtro"
          >
            Limpar
          </button>
        )}
        <div className="todos-dados-field" title="Exige senha de auditoria para ver CPF, celular, MAC e IPs">
          <span className="todos-dados-label">Todos os dados</span>
          <label className="todos-dados-check-wrap">
            <input
              type="checkbox"
              checked={mostrarTodosDados}
              onChange={(e) => onToggleTodosDados(e.target.checked)}
            />
          </label>
        </div>
      </div>

      {modalAuditoriaAberto && (
        <div className="auditoria-modal-overlay" role="presentation" onClick={fecharModalAuditoria}>
          <div
            className="auditoria-modal"
            role="dialog"
            aria-labelledby="auditoria-modal-title"
            onClick={(ev) => ev.stopPropagation()}
          >
            <h2 id="auditoria-modal-title">Senha de auditoria</h2>
            <p className="auditoria-modal-desc">Digite a senha para exibir todos os campos da lista.</p>
            <form onSubmit={confirmarSenhaAuditoria}>
              <label htmlFor="senha-auditoria" className="auditoria-senha-label">Senha</label>
              <input
                id="senha-auditoria"
                type="password"
                className="auditoria-senha-input"
                value={senhaAuditoria}
                onChange={(e) => {
                  setSenhaAuditoria(e.target.value);
                  setErroSenhaAuditoria('');
                }}
                autoComplete="off"
                autoFocus
              />
              {erroSenhaAuditoria && (
                <div className="auditoria-erro" role="alert">{erroSenhaAuditoria}</div>
              )}
              <div className="auditoria-modal-actions">
                <button type="button" className="auditoria-btn-cancel" onClick={fecharModalAuditoria}>
                  Cancelar
                </button>
                <button type="submit" className="auditoria-btn-ok">Confirmar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {acessos.length === 0 ? (
        <div className="no-acessos">
          <FiUsers size={48} />
          <p>Nenhum acesso registrado</p>
        </div>
      ) : acessosFiltrados.length === 0 ? (
        <div className="no-acessos">
          <FiUsers size={48} />
          <p>Nenhum acesso encontrado para a data selecionada</p>
        </div>
      ) : (
        <div className={`acessos-table-container${mostrarTodosDados ? ' acessos-table-full' : ''}`}>
          <table className="acessos-table">
            <thead>
              <tr>
                <th>Nome</th>
                <th>Hotspot/Torre</th>
                {mostrarTodosDados && (
                  <>
                    <th>CPF</th>
                    <th>Celular</th>
                    <th>MAC</th>
                    <th>IP</th>
                    <th>IP Público</th>
                  </>
                )}
                <th>Data</th>
                {mostrarTodosDados && <th>Hora</th>}
              </tr>
            </thead>
            <tbody>
              {acessosFiltrados.map((acesso) => (
                <tr key={acesso.id}>
                  <td>{acesso.nome}</td>
                  <td className="mono">{acesso.server_name_resolvido || acesso.server_name || '-'}</td>
                  {mostrarTodosDados && (
                    <>
                      <td>{acesso.cpf}</td>
                      <td>{acesso.celular}</td>
                      <td className="mono">{acesso.mac_address}</td>
                      <td className="mono">{acesso.ip_address}</td>
                      <td className="mono">{acesso.public_ip || '-'}</td>
                    </>
                  )}
                  <td>{formatDateOnly(acesso.data_acesso)}</td>
                  {mostrarTodosDados && <td>{acesso.hora_acesso || '-'}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ListarAcessos;
